import { Component } from '@angular/core';

import { TranslatePipe } from '@camelot/translation';
import { ButtonComponent, TextComponent } from '@camelot/ui';

import { CamTranslationGrid } from '../../translation.service';
import { AbstractGridComponent } from '../abstract-grid.component';

type PageNumber = {
  number: number;
  isEllipsis?: boolean;
};
@Component({
  imports: [ButtonComponent, TextComponent, TranslatePipe],
  selector: 'cam-grid-pagination',
  styleUrl: './pagination.component.scss',
  templateUrl: './pagination.component.html',
})
export class PaginationComponent extends AbstractGridComponent<any> {
  readonly maxPageNumber = 10;
  readonly PageNumber!: { pagenumber: PageNumber };

  // Tout est lu depuis le signal `pagination` de la grille, jamais directement depuis
  // `grid.api` : l'API AG Grid vit hors d'Angular et ne déclencherait aucun rendu.
  get currentPage() {
    return this.grid.pagination().currentPage + 1;
  }
  get grid() {
    return this._grid;
  }
  get pageSize() {
    return this.grid.pagination().pageSize || 10;
  }
  get rangeEnd() {
    return Math.min(this.currentPage * this.pageSize, this.totalRows);
  }
  get rangeStart() {
    return this.totalRows === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
  }
  /**
   * Seule condition d'affichage du template — et volontairement pas `grid.api`.
   * Au premier rendu l'API AG Grid est encore `null` : un `@if (grid.api && show)`
   * court-circuiterait avant de lire le signal, aucune dépendance ne serait
   * enregistrée, et la vue ne serait plus jamais réévaluée en mode zoneless.
   * `totalPages > 1` implique de toute façon que la grille est prête et paginée.
   */
  get show() {
    return this.totalPages > 1;
  }
  get totalPages() {
    return this.grid.pagination().totalPages;
  }
  get totalRows() {
    return this.grid.pagination().totalRows;
  }

  constructor() {
    super();
    CamTranslationGrid.getInstance();
  }

  public getListPage(): PageNumber[] {
    const total = this.totalPages;
    if (total <= 1) {
      return [];
    }

    const current = this.currentPage;
    const max = this.maxPageNumber;

    if (total <= max) {
      return this._range(1, total);
    }

    const pages: PageNumber[] = [{ number: 1 }];
    const siblings = 1;
    const leftSibling = Math.max(current - siblings, 2);
    const rightSibling = Math.min(current + siblings, total - 1);

    if (leftSibling > 2) {
      pages.push({ isEllipsis: true, number: -1 });
    }

    for (let i = leftSibling; i <= rightSibling; i++) {
      pages.push({ number: i });
    }

    if (rightSibling < total - 1) {
      pages.push({ isEllipsis: true, number: -2 });
    }

    pages.push({ number: total });
    return pages;
  }

  public goToNext() {
    this.grid.api?.paginationGoToNextPage();
  }
  public goToPage(page: number) {
    this.grid.api?.paginationGoToPage(page - 1);
  }
  public goToPrevious() {
    this.grid.api?.paginationGoToPreviousPage();
  }

  public track(_: number, item: PageNumber) {
    return item.isEllipsis ? `e${item.number}` : item.number;
  }

  private _range(start: number, end: number): PageNumber[] {
    const pages: PageNumber[] = [];
    for (let i = start; i <= end; i++) {
      pages.push({ number: i });
    }
    return pages;
  }
}
